import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import type { Money } from '@nucleus-commerce/core';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Form } from '@nucleus-commerce/ui/components/form';

import {
  DeleteVariantsDialog,
  ProductScopeProvider,
  revealFor,
  ScopeChip,
  SetVariantPriceDialog,
  useProductScope,
  useVariantRowsForm,
  useVariants,
  VariantFooterActions,
  VariantsSection,
  VariantStatusBulkActions,
  WatchedVariantStatus,
  type VariantsCardExtension,
} from '@/features/products';
import { session } from '@/shared/session/session';

import { storeCatalogKeys } from '../hooks/store-catalog-keys';
import { MarketPriceDraftsContext, useMarketPriceDrafts } from '../hooks/use-market-price-drafts';
import { StorePricingContext, useStorePricingDrafts } from '../hooks/use-store-pricing-drafts';
import { useStoreVariantColumns } from '../hooks/use-store-variant-columns';

import { MarketPricesModal } from './market-prices-modal';
import { StoreVariantDetails } from './store-variant-details';

export interface StoreVariantsSectionProps {
  storeId: string;
  storeProductId: string;
  /** The master product; its variants are the rows. */
  product: Product;
  /** The store currency; every store price is entered in it. */
  currency: string;
}

/** Draft fields a store row shows without View more. */
const ROW_DRAFT_FIELDS: ReadonlySet<string> = new Set(['price', 'skuOverride', 'isAvailable']);
const NO_VARIANTS: readonly Variant[] = [];
/** The row's Price and SKU are the store's; every master field sits under View more here. */
const NO_MASTER_ROW_FIELDS: ReadonlySet<string> = new Set();

/**
 * The store product page's Variants card: the master's rows with this store's price, SKU override,
 * stock and Sell here. Store prices, market prices and the shared master fields each keep their own
 * drafts and save bar entry, so one Save writes them all and a failure keeps its own error and
 * values. Stock changes at once through its own modal.
 */
export function StoreVariantsSection({
  storeId,
  storeProductId,
  product,
  currency,
}: StoreVariantsSectionProps) {
  const { t } = useTranslation(['storeCatalog', 'errors']);
  const message = useErrorMessage();
  const scope = useProductScope();
  const { me } = session.useSession();
  const storeName = me?.stores.find((store) => store.id === storeId)?.name ?? scope.storeName;
  const pricing = useStorePricingDrafts(storeId, storeProductId, currency);
  const markets = useMarketPriceDrafts(storeId, storeProductId, product.id);
  const columns = useStoreVariantColumns(storeId, product.id, currency);
  const variantsQuery = useVariants(product.id);
  const variants = variantsQuery.data ?? NO_VARIANTS;
  const master = useVariantRowsForm(product, variants, { rowFields: NO_MASTER_ROW_FIELDS });

  // The rows' base and inherited prices and master SKUs are the master's: follow it when it moves.
  const queryClient = useQueryClient();
  const seenList = useRef(variantsQuery.data);
  useEffect(() => {
    const before = seenList.current;
    seenList.current = variantsQuery.data;
    if (before && before !== variantsQuery.data) {
      void queryClient.invalidateQueries({
        queryKey: storeCatalogKeys.pricing(storeId, storeProductId),
      });
    }
  }, [queryClient, storeId, storeProductId, variantsQuery.data]);

  const [marketsOpen, setMarketsOpen] = useState(false);
  const [settingPrice, setSettingPrice] = useState<Variant[]>([]);
  const missingRate = pricing.rows.some((row) => row.effective === null);
  const { saveError, queryError, rowErrors } = pricing;
  const failedMarkets = [...markets.failures.keys()].join();
  const order = pricing.rows.map((row) => row.variantId).join();

  const { errorIds: storeErrorIds, reveal: storeReveal } = useMemo(() => {
    const fields = new Map<string, string[]>();
    for (const [id, error] of rowErrors) {
      fields.set(id, [ROW_DRAFT_FIELDS.has(error.field) ? 'price' : error.field]);
    }
    for (const id of failedMarkets.split(',').filter(Boolean)) {
      fields.set(id, [...(fields.get(id) ?? []), 'market']);
    }
    return {
      errorIds: new Set(fields.keys()),
      reveal: revealFor(order.split(','), fields),
    };
  }, [rowErrors, failedMarkets, order]);
  const masterReveal = master.reveal;
  const reveal = useMemo(() => masterReveal ?? storeReveal, [masterReveal, storeReveal]);
  const errorIds = new Set([...storeErrorIds, ...master.errorIds]);

  const notice =
    queryError || missingRate || saveError || master.problems.length > 0 ? (
      <div className="flex flex-col gap-3 px-5 pt-4">
        {queryError ? <ErrorState error={queryError} onRetry={pricing.refetch} compact /> : null}
        {missingRate ? (
          <Alert>
            <AlertDescription>
              {t('storeCatalog:pricing.noRateHint', { currency })}
            </AlertDescription>
          </Alert>
        ) : null}
        {saveError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(saveError)}
              {isApiError(saveError) && saveError.requestId ? (
                <span className="block text-xs">
                  {t('errors:requestIdShort', { requestId: saveError.requestId })}
                </span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        {master.problems.map((text) => (
          <Alert key={text} variant="destructive" role="alert">
            <AlertDescription>{text}</AlertDescription>
          </Alert>
        ))}
      </div>
    ) : null;

  const patchRows = (rows: readonly Variant[], patch: { price?: Money; isAvailable?: boolean }) => {
    for (const variant of rows) {
      const row = pricing.rowOf(variant.id);
      if (row) pricing.setDraft(variant.id, { ...pricing.draftFor(row), ...patch });
    }
  };

  const ownPrices = pricing.rows.filter((row) => pricing.draftFor(row).price !== null).length;

  const extension: VariantsCardExtension = {
    variants: master.shown,
    // The render between a first list arriving and the form holding it.
    isPending: variantsQuery.isPending || (master.shown.length === 0 && variants.length > 0),
    columns,
    selectable: pricing.canWrite,
    badge: <ScopeChip scope="store" />,
    description: t('storeCatalog:storeVariants.description', { store: storeName ?? '' }),
    notice,
    errorIds,
    reveal,
    emptyText: t('storeCatalog:storeVariants.empty'),
    skuOf: (variant) => {
      const row = pricing.rowOf(variant.id);
      return `${master.skuOf(variant)} ${row ? pricing.draftFor(row).skuOverride : ''}`;
    },
    renderStatus: (variant) => <WatchedVariantStatus id={variant.id} />,
    renderRowNote: (variant, expanded) => {
      if (expanded) return null;
      const pricingError = rowErrors.get(variant.id);
      const text =
        master.hiddenError(variant.id) ??
        markets.failures.get(variant.id) ??
        (pricingError && !ROW_DRAFT_FIELDS.has(pricingError.field) ? pricingError.message : null);
      return text ? (
        <p role="alert" className="text-destructive text-xs">
          {text}
        </p>
      ) : null;
    },
    renderDetail: (variant) => (
      <StoreVariantDetails
        storeId={storeId}
        storeProductId={storeProductId}
        product={product}
        variant={variant}
      />
    ),
    renderBulk: (selected) => (
      <>
        <Button
          type="button"
          size="sm"
          className="h-8 text-[13px]"
          onClick={() => setSettingPrice(selected)}
        >
          {t('storeCatalog:storeVariants.bulk.setPrice')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-[13px]"
          onClick={() => patchRows(selected, { isAvailable: true })}
        >
          {t('storeCatalog:storeVariants.bulk.sell')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-[13px]"
          onClick={() => patchRows(selected, { isAvailable: false })}
        >
          {t('storeCatalog:storeVariants.bulk.stopSelling')}
        </Button>
        <span
          role="group"
          aria-label={t('storeCatalog:storeVariants.bulk.everyStore')}
          className="border-border flex flex-wrap items-center gap-1.5 border-l pl-2"
        >
          <ScopeChip scope="shared" />
          <VariantStatusBulkActions rows={master} selected={selected} />
        </span>
      </>
    ),
    footer: (
      <>
        <span className="min-w-0 flex-1 basis-60">
          {t('storeCatalog:storeVariants.ownPrices', { count: ownPrices })}
          {' · '}
          <Button
            type="button"
            variant="link"
            size="xs"
            className="text-primary h-auto p-0 text-[13px]"
            disabled={pricing.rows.length === 0}
            onClick={() => setMarketsOpen(true)}
            data-tour="store-catalog.product.market-prices"
          >
            {t('storeCatalog:marketPrices.open')}
          </Button>
        </span>
        <VariantFooterActions product={product} variants={variants} />
      </>
    ),
  };

  return (
    <ProductScopeProvider value={{ ...scope, productId: product.id, storeName }}>
      <StorePricingContext value={pricing}>
        <MarketPriceDraftsContext value={markets}>
          <Form {...master.form}>
            <div id="pricing" data-tour="store-catalog.product.pricing">
              <VariantsSection product={product} extension={extension} />
            </div>
            <DeleteVariantsDialog
              rows={master}
              note={t('storeCatalog:storeVariants.deleteEveryStore')}
            />
          </Form>
        </MarketPriceDraftsContext>
        <SetVariantPriceDialog
          count={settingPrice.length}
          currency={currency}
          onApply={(price) => {
            patchRows(settingPrice, { price });
            setSettingPrice([]);
          }}
          onClose={() => setSettingPrice([])}
        />
        <MarketPricesModal
          storeId={storeId}
          storeProductId={storeProductId}
          productId={product.id}
          variants={pricing.rows}
          open={marketsOpen}
          onOpenChange={setMarketsOpen}
        />
      </StorePricingContext>
    </ProductScopeProvider>
  );
}
